import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Eye, Loader2 } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { can } from "@/components/crm/helpers";
import { SiteHistory } from "./SiteHistory";

const ST = (s) => (s.live_url ? ["Online", "bg-emerald-50 text-emerald-700"] : s.suspended ? ["Sospeso", "bg-amber-50 text-amber-800"]
  : s.preview_url ? ["In anteprima · non pubblicato", "bg-sky-50 text-sky-700"] : ["In preparazione", "bg-slate-100 text-slate-600"]);
const abs = (u) => (u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

// Real public address only (served online version, custom domain when active); otherwise the authorised preview; never the editor.
function SiteLink({ s }) {
  const live = !!s.live_url, url = s.live_url || s.preview_url;
  if (!url) return null;
  const Icon = live ? ExternalLink : Eye;
  return (
    <Button asChild className="h-10" variant={live ? "default" : "outline"}>
      <a href={abs(url)} target="_blank" rel="noopener noreferrer" aria-label={`${live ? "Visualizza il sito" : "Visualizza anteprima"} ${s.name} (si apre in una nuova scheda)`} data-testid={`area-site-${live ? "view" : "preview"}-${s.id}`}>
        <Icon size={15} aria-hidden />{live ? "Visualizza il sito" : "Visualizza anteprima"}</a>
    </Button>);
}

// Sites of this space: status, change requests to ADM. Self-service «Anteprima → Pubblica» is prepared but NOT active in DEV (stated, no fake button).
export default function AreaSite({ tid, role, go }) {
  const api = useApi();
  const [sites, setSites] = useState(null);
  useEffect(() => { api.get(`/t/${tid}/sites`).then(({ data }) => setSites(data)).catch((e) => { setSites([]); toast.error(errMsg(e)); }); }, [api, tid]);
  if (!sites) return <Loader2 className="animate-spin text-slate-400" />;
  const ask = can(role, "support.create");
  return (
    <div className="space-y-3" data-testid="area-site">
      {sites.length === 0 && <p className="text-sm text-slate-500" data-testid="area-site-empty">Nessun sito collegato a questo spazio.</p>}
      {sites.map((s) => { const [l, cls] = ST(s); return (
        <section key={s.id} className="space-y-2 rounded-lg border border-slate-200 bg-white p-4" data-testid={`area-site-${s.id}`}>
          <div className="flex flex-wrap items-center gap-2"><p className="text-sm font-medium text-slate-800">{s.name}</p><span className={`rounded px-1.5 py-0.5 text-[11px] ${cls}`} data-testid={`area-site-status-${s.id}`}>{l}</span></div>
          <div className="flex flex-wrap items-center gap-2">
          <SiteLink s={s} />
          {ask ? <Button variant="outline" className="h-10" onClick={() => go("support", { type: "site_change", site_id: s.id, title: `Modifica al sito ${s.name}` })} data-testid={`area-site-change-${s.id}`}>Chiedi una modifica</Button>
            : <p className="text-xs text-slate-500">Il tuo ruolo non può aprire richieste di modifica.</p>}
          </div>
          {s.live_url && <p className="break-all text-xs text-slate-500" data-testid={`area-site-url-${s.id}`}>{s.domain?.active ? s.domain.host : abs(s.live_url).replace(/^https?:\/\//, "").replace(/\/$/, "")}</p>}
          {s.domain && !s.domain.active && <p className="text-xs text-amber-800" data-testid={`area-site-domain-pending-${s.id}`}>Dominio {s.domain.host} in attivazione: finché non è pronto il sito resta raggiungibile all'indirizzo MUSORA.</p>}
          {!s.live_url && s.preview_url && <p className="text-xs text-slate-500">Anteprima riservata al tuo team: non condividerla, il sito non è ancora pubblicato.</p>}
          <SiteHistory tid={tid} site={s} role={role} />
        </section>); })}
      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="area-site-publish-note">
        <b>Pubblicazione in autonomia</b> (anteprima → pubblica): predisposta, non ancora attiva. Lo storico versioni e il ripristino della bozza sono disponibili; la pubblicazione la fa MUSORA.</p>
    </div>);
}
